import type { FC } from 'react';

export type DoctoralSupportKind = 'duty' | 'calendar' | 'notebook' | 'group' | 'surveys';

type Props = {
  kind: DoctoralSupportKind;
  onClose: () => void;
  groupName?: string;
  programTitle?: string;
  yearSemester?: string;
  onOpenTools?: () => void;
};

const CONTENT: Record<
  DoctoralSupportKind,
  { title: string; subtitle: string; items: Array<{ title: string; meta: string; tag: string }> }
> = {
  duty: {
    title: 'Дежурство на кафедре',
    subtitle: 'График академических и кураторских дежурств',
    items: [
      { title: 'Четверг · 14:00–18:00', meta: 'Корпус №2, каб. 312 · журнал у секретаря кафедры', tag: 'БЛИЖАЙШЕЕ' },
      { title: 'Понедельник · 10:00–12:00', meta: 'Консультационный час для 1 курса', tag: 'ПЛАН' },
      { title: 'Среда · 16:00–18:00', meta: 'Сопровождение абитуриентов PhD', tag: 'ПЛАН' },
    ],
  },
  calendar: {
    title: 'Служебный академический календарь',
    subtitle: 'Защиты, заседания кафедры и контрольные точки',
    items: [
      { title: '25.05.2026 — Заседание кафедры', meta: 'Аттестация НИРД 4 семестра', tag: 'АТТЕСТАЦИЯ' },
      { title: '10.06.2026 — Отчёт по практике', meta: 'Сдача исследовательской практики', tag: 'СРОК' },
      { title: '01.10.2026 — Зарубежная стажировка', meta: 'Старт программы стажировки', tag: 'СТАЖИРОВКА' },
    ],
  },
  notebook: {
    title: 'Академический блокнот',
    subtitle: 'Гипотезы, структура глав и вопросы консультантам',
    items: [
      { title: 'Глава 2 · черновик структуры', meta: 'Сравнительный анализ морфологических анализаторов', tag: 'ЧЕРНОВИК' },
      { title: 'Вопросы зарубежному консультанту', meta: 'Уточнение методики по корпусной выборке', tag: 'ВОПРОС' },
      { title: 'Гипотеза H1', meta: 'Частотность n-грамм коррелирует с устойчивостью термина', tag: 'ГИПОТЕЗА' },
    ],
  },
  group: {
    title: 'Учебная группа · Староста',
    subtitle: 'Контингент и контакты группы докторантов',
    items: [
      { title: 'Староста группы', meta: 'Касымов Ерлан · +7 775 123-45-67', tag: 'СТАРОСТА' },
      { title: 'Состав группы', meta: '8 докторантов · куратор программы: доцент Омарова Г.К.', tag: 'СОСТАВ' },
      { title: 'Общий чат группы', meta: 'Оперативные объявления и обмен материалами', tag: 'СВЯЗЬ' },
    ],
  },
  surveys: {
    title: 'Академические опросы',
    subtitle: 'Анкетирование качества и условий НИР',
    items: [
      {
        title: 'Оценка доступности лабораторного оборудования',
        meta: 'Активен до 15 мая · результаты сохраняются с provenance',
        tag: 'АКТИВЕН',
      },
      {
        title: 'Условия научно-исследовательской деятельности PhD',
        meta: 'Анонимный опрос кафедры',
        tag: 'ЧЕРНОВИК',
      },
    ],
  },
};

export const DoctoralSupportOverlay: FC<Props> = ({
  kind,
  onClose,
  groupName,
  programTitle,
  yearSemester,
  onOpenTools,
}) => {
  const block = CONTENT[kind];
  const contextLine = [programTitle, yearSemester, groupName].filter(Boolean).join(' · ');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-[#071927] border border-cyan-500/40 rounded-xl shadow-[0_0_35px_rgba(6,182,212,0.25)] overflow-hidden">
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-cyan-500/20 bg-[#061421]">
          <div>
            <p className="text-[10px] font-bold tracking-[0.14em] text-cyan-400 uppercase">Doctoral · support</p>
            <h3 className="text-lg font-semibold text-white mt-1">{block.title}</h3>
            <p className="text-xs text-slate-400 mt-1">{block.subtitle}</p>
            {contextLine ? <p className="text-[11px] text-slate-500 mt-2">{contextLine}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg border border-white/10 text-slate-300 hover:text-white"
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>

        <div className="p-5 space-y-3 max-h-[60vh] overflow-auto">
          {block.items.map((item) => (
            <div
              key={item.title}
              className="p-3 rounded-lg border border-cyan-500/20 bg-cyan-950/20 flex items-start justify-between gap-3"
            >
              <div>
                <strong className="text-sm text-white">{item.title}</strong>
                <p className="text-xs text-slate-400 mt-1">{item.meta}</p>
              </div>
              <span className="text-[10px] font-bold tracking-wide text-cyan-300 shrink-0">{item.tag}</span>
            </div>
          ))}
        </div>

        {kind === 'surveys' && onOpenTools ? (
          <footer className="px-5 py-4 border-t border-cyan-500/20 flex justify-end">
            <button
              type="button"
              className="px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 text-xs font-bold"
              onClick={() => {
                onClose();
                onOpenTools();
              }}
            >
              Открыть опросы в Research Tools →
            </button>
          </footer>
        ) : null}
      </div>
    </div>
  );
};
